export interface ItemError {
    name?: string;
    message?: string;
}

/**
 * Extracts the human-readable message from a failed SharePoint REST / Graph call.
 * Returns undefined when the response carries no usable message (network failure,
 * HTML error page, unexpected shape) so callers can fall back to a localized string.
 */
export const getApiErrorMessage = (error: any): string | undefined => {
    const message =
        error?.data?.["odata.error"]?.message?.value // SharePoint REST, odata=nometadata
        ?? error?.data?.error?.message?.value        // SharePoint REST, odata=verbose
        ?? error?.data?.error?.message;              // Graph

    return typeof message === "string" && message.trim() ? message : undefined;
};

const MAX_DETAIL_LINES = 5;
const MAX_NAMES_PER_LINE = 3;

/**
 * Builds a display message for one or more per-item failures.
 * A single failure with a server message shows that message directly; multiple
 * failures get the fallback as headline with one line per distinct cause.
 * Identical messages are grouped and the output is capped so a large bulk
 * failure cannot produce an unbounded dialog.
 */
export const buildItemErrorMessage = (errors: ItemError[], fallbackMessage: string): string => {
    if (!errors?.some(error => error.message)) {
        return fallbackMessage;
    }

    if (errors.length === 1) {
        return errors[0].message;
    }

    const namesByMessage = new Map<string, string[]>();
    errors.forEach(error => {
        const message = error.message ?? fallbackMessage;
        const names = namesByMessage.get(message) ?? [];
        if (error.name) {
            names.push(error.name);
        }
        namesByMessage.set(message, names);
    });

    const groups = Array.from(namesByMessage.entries());
    const lines = groups.slice(0, MAX_DETAIL_LINES).map(([message, names]) => {
        if (names.length === 0) {
            return message;
        }
        const hiddenNameCount = names.length - MAX_NAMES_PER_LINE;
        const shownNames = names.slice(0, MAX_NAMES_PER_LINE).join(", ");
        return `${shownNames}${hiddenNameCount > 0 ? ` (+${hiddenNameCount})` : ""}: ${message}`;
    });

    if (groups.length > MAX_DETAIL_LINES) {
        lines.push(`… (+${groups.length - MAX_DETAIL_LINES})`);
    }

    return [fallbackMessage, ...lines].join("\n");
};
